import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { REGISTRY } from "@/constants/testIds";
import { useLang } from "@/i18n/LanguageContext";
import { OrchidFlourish, RoseFlourish, DahliaFlourish, HydrangeaFlourish, CallaLilyFlourish } from "@/components/wedding/Flourish";

export const Registry = () => {
  const { t } = useLang();
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const heading = t("registry.heading");
  const accounts = t("registry.accounts") || [];

  const copy = async (idx, iban) => {
    try {
      await navigator.clipboard.writeText(iban.replace(/\s/g, ""));
      setCopiedIdx(idx);
      toast.success(t("registry.copied"));
      setTimeout(() => setCopiedIdx(-1), 1800);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <section id="registry" data-testid={REGISTRY.section} className="relative py-24 sm:py-32 bg-[#0A0A0A] text-[#FAFAFA] overflow-hidden">
      <OrchidFlourish
        className="absolute -top-6 -right-10 w-[100px] sm:w-[140px] md:w-[180px] scale-x-[-1]"
        opacity={0.85}
        variant="dark"
      />
      <RoseFlourish
        className="absolute -bottom-8 -left-6 w-[100px] sm:w-[140px] md:w-[180px] rotate-6"
        opacity={0.85}
        variant="dark"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-7">
            <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light">
              {heading[0]}<em className="italic">{heading[1]}</em>{heading[2]}
            </h2>
          </div>
          <div className="md:col-span-5 flex md:items-end">
            <p className="font-serif italic text-lg text-white/70 max-w-md">{t("registry.sub")}</p>
          </div>
        </div>

        {/* IBAN section */}
        <div className="border-t border-white/10 pt-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 items-end">
            <div className="md:col-span-7">
              <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light">
                {t("registry.ibanTitle")}
              </h3>
            </div>
            <div className="md:col-span-5">
              <p className="font-serif italic text-lg text-white/65 max-w-md">{t("registry.ibanSub")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px bg-white/10 max-w-md">
            {accounts.map((a, i) => (
              <div
                key={i}
                data-testid={REGISTRY.iban(i)}
                className="bg-[#0A0A0A] p-8 sm:p-10 flex flex-col gap-4"
              >
                <div className="font-overline text-[10px] text-white/50">Konto</div>
                <div className="font-title text-2xl sm:text-3xl tracking-tight">{a.name}</div>
                <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-6">
                  <div className="font-mono-acc text-lg sm:text-xl tracking-wider text-[color:var(--sunset-300)]">
                    {a.iban}
                  </div>
                  <button
                    data-testid={REGISTRY.ibanCopy(i)}
                    onClick={() => copy(i, a.iban)}
                    className="inline-flex items-center gap-2 border border-white/20 hover:border-[color:var(--sunset-300)] hover:text-[color:var(--sunset-300)] font-overline text-[10px] px-4 py-2.5 transition-colors shrink-0"
                    aria-label="Copy IBAN"
                  >
                    {copiedIdx === i ? <Check size={12} /> : <Copy size={12} />}
                    {copiedIdx === i ? t("registry.copied") : t("registry.copy")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
